/**
 * Типы гостевых заказов F&B и фолио/счёта гостя (UC-G.09 / UC-G.13).
 *
 * Контракт — из ЖИВОГО бэкенда (demo, 2026-06-16):
 *   - Заказ еды/напитков в номер = service-request в F&B-категории
 *     (`room_service` «Еда и напитки в номер», `minibar`). Серверной
 *     корзины НЕТ — корзина чисто клиентская, на checkout отправляем по
 *     одной заявке `POST /v1/requests` на позицию.
 *   - «Мои заказы / лента заявок» — GET /v1/me/requests/ → { items, count }.
 *   - Фолио/счёт — GET /v1/folios/* пока возвращает 501 (модуль billing
 *     scaffolded, issue #338). Поэтому FolioPage строит «начисления» из
 *     собственных оплачиваемых заявок гостя, а раздел «официальный счёт
 *     отеля» помечает явной заглушкой до выката billing.
 *
 * Поля — snake_case как в остальных HTTP DTO проекта. При выходе billing
 * Sprint 1 (folio-контракт) сверить FolioResponse здесь.
 */

/* ===================== Лента заявок гостя (/v1/me/requests) ===================== */

/**
 * Строка ленты `/v1/me/requests/`. Совпадает с RequestResponse бэкенда
 * (internal/request/interfaces/http/dto.go) в части, нужной витрине гостя.
 */
export interface GuestRequest {
  id: string;
  tenant_id: string;
  guest_user_id: string;
  /** Легаси-слаг категории (`housekeeping`, `room_service`, `minibar`, …). */
  category: string;
  /** FK на service_categories (v2). */
  category_id?: string;
  item_id?: string;
  variant_id?: string;
  title: string;
  description?: string;
  priority: string;
  /** created | assigned | in_progress | resolved | cancelled | … */
  state: string;
  assigned_staff_id?: string;
  sla_due_at?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
}

export interface GuestRequestsListResponse {
  items: GuestRequest[];
  count: number;
}

/* ===================== F&B-категории (фильтр меню) ===================== */

/**
 * Коды категорий каталога, которые на витрине показываем как «Меню / F&B».
 * Гость заказывает еду и напитки в номер именно из них. Список — стабильные
 * seed-слаги (см. живой GET /v1/service-catalog на demo/mare).
 */
export const FNB_CATEGORY_CODES: readonly string[] = ['room_service', 'minibar'];

/** Эвристика: категория относится к F&B (еда/напитки/бар/ресторан/кухня). */
export function isFnbCategoryCode(code: string): boolean {
  if (FNB_CATEGORY_CODES.includes(code)) return true;
  const c = code.toLowerCase();
  return (
    c.includes('food') ||
    c.includes('drink') ||
    c.includes('bar') ||
    c.includes('menu') ||
    c.includes('restaurant') ||
    c.includes('kitchen') ||
    c.includes('breakfast') ||
    c.includes('beverage')
  );
}

/* ===================== Фолио / счёт гостя (/v1/folios, #338) ===================== */

/**
 * Минимальный контракт фолио на будущее (billing Sprint 1). Пока эндпойнт
 * отвечает 501 — UI помечает блок заглушкой. Поля — предположительные, при
 * выкате billing привести в соответствие.
 */
export interface FolioLine {
  id: string;
  description: string;
  amount_minor: number;
  currency: string;
  posted_at?: string;
  kind?: string;
}

export interface FolioResponse {
  id: string;
  stay_id?: string;
  status?: string;
  currency: string;
  balance_minor: number;
  lines: FolioLine[];
}
